/**
 * PER-MAP EXTRAS — the plug-in point for map-specific visual improvement
 * passes (2026-11-04, owner: "one shell per map to improve each map").
 *
 * Each map's pass lives in its OWN file, `mapExtras/<mapId>.ts`, and is picked
 * up automatically (Vite `import.meta.glob`) — so passes on different maps
 * never edit the same file. A module exports:
 *
 *   export const mapIds = ["village"];                     // maps it dresses
 *   export function build(ctx: MapExtrasContext): THREE.Object3D | null;
 *
 * `build` runs ONCE at map load, under the load screen, and must return
 * STATIC geometry (merged / instanced meshes; the renderer freezes the
 * matrices like the rest of the map). No per-frame work, no per-frame
 * allocation. VISUAL ONLY: colliders are the shared map boxes (server
 * authoritative) and must not change; dressing on walls stays ≤ 0.26 m proud
 * (the mapDressing contract) and must never look standable where it is not.
 */
import type * as THREE from "three";
import type { MapDef } from "@neon-purge/shared";

export interface MapExtrasContext {
  map: MapDef;
  theme: "day" | "neon";
  /** Renderer has shadows on (cast shadows only when true). */
  shadows: boolean;
  /** Performance mode: build a lighter variant (fewer pieces), never nothing. */
  lowSpec: boolean;
}
interface ExtrasModule { mapIds: readonly string[]; build(ctx: MapExtrasContext): THREE.Object3D | null }

let mods: Record<string, ExtrasModule> = {};
try {
  mods = import.meta.glob<ExtrasModule>("./mapExtras/*.ts", { eager: true });
} catch {
  // esbuild-bundled node benches have no import.meta.glob: no extras there
  mods = {};
}

/** Every extras group for this map (possibly none). Never throws. */
export function buildMapExtras(ctx: MapExtrasContext): THREE.Object3D[] {
  const out: THREE.Object3D[] = [];
  for (const [path, m] of Object.entries(mods)) {
    if (!m?.mapIds?.includes(ctx.map.id)) continue;
    try {
      const g = m.build(ctx);
      if (g) { g.name = g.name || `extras:${path}`; out.push(g); }
    } catch (e) {
      console.error(`[map extras] ${path} failed — map loads without it`, e);
    }
  }
  return out;
}
